import { LedgerActivityReportItem, LedgerActivityReportLedgerType } from '@mews-commander/data-access';
import { Stack, Table, TableHeader, TableProvider, useTableState } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { useLedgerActivityTableMapping } from '../hooks/useLedgerActivityTableMapping';
import { LedgerActivityReportUrlSearchValues, useDefaultLedgerActivityReportFilterValues } from '../schema';
import { useLedgerActivityReportQuery } from '../hooks/useLedgerActivityReportQuery';
import { useTableUrlStateChange } from '../hooks/useTableUrlStateChange';

interface LedgerActivityReportTableProps {
    ledgerType: LedgerActivityReportLedgerType;
    title?: React.ReactNode;
    filterValues: LedgerActivityReportUrlSearchValues;
}

export const LedgerActivityReportTable = ({ ledgerType, title, filterValues }: LedgerActivityReportTableProps) => {
    const {
        data: { ledgerActivityReportItems, ledgerTotal },
    } = useLedgerActivityReportQuery({ ledgerType, filterValues });

    const defaultLedgerActivityReportFilterValues = useDefaultLedgerActivityReportFilterValues();
    const [tableState, setTableState] = useTableState<LedgerActivityReportItem>({
        pagination: {
            show: false,
        },
        grouping: ['accountingCategory'],
        sort: filterValues.sort ?? defaultLedgerActivityReportFilterValues.sort,
    });

    const handleTableStateChange = useTableUrlStateChange({
        tableState,
        setTableState,
        filterValues,
    });

    const columns = useLedgerActivityTableMapping({ ledgerTotal });

    const showTaxColumns =
        ledgerType === LedgerActivityReportLedgerType.NonRevenue ||
        ledgerType === LedgerActivityReportLedgerType.Outlets;

    return (
        <TableProvider
            columns={columns}
            state={{ ...tableState, sort: filterValues.sort ?? defaultLedgerActivityReportFilterValues.sort }}
            onStateChange={handleTableStateChange}
        >
            <Stack vertical>
                {title ? <TableHeader title={title} /> : null}
                <Table
                    virtualized
                    maxHeight="60vh"
                    fixedHeader
                    data={ledgerActivityReportItems ?? []}
                    uniqueRowId="id"
                    amountOfRows={ledgerActivityReportItems?.length ?? 0}
                    emptyState={{ primaryText: lt('NoDataAvailable') }}
                    columnVisibility={{
                        accountingCategory: false,
                        taxRate: showTaxColumns,
                        netBaseValue: showTaxColumns,
                    }}
                    showFooter
                />
            </Stack>
        </TableProvider>
    );
};
